/*
Topic registry reference

Each topic opens a curated cross-law modal. The app does not run a keyword search here:
you manually decide which document sections are relevant to each topic.

Visibility is inferred from documentId:
- UAE/federal/guideline documents show for UAE organisations across Mainland, DIFC and ADGM.
- dfsa-aml-module shows only for DIFC/DFSA organisations.
- fsra-aml-rulebook shows only for ADGM/FSRA organisations.
- FATF/global documents show for all jurisdictions.

Supported reference shapes:
- Selected paragraphs or nested bullets, with list context:
    { sectionNumber: "6.4.1", textStartsWithAny: ["If a customer", "With regard to identified Domestic PEPs"] }
- Multiple definitions in one excerpt:
    { sectionNumber: "3.2", definitionTerms: ["source of funds", "source of wealth"] }
- Whole chapter:
    { chapter: "Chapter Four" }
    { chapterTitle: "Reporting and Compliance" }
- Whole article/top-level section:
    { articleNumber: "24" }
    { articleLabel: "Article (24)" }
    { sectionNumber: "7.3" }
    { sectionLabel: "Section 7.3" }
- Embedded sub-section inside FI/DNFBP guideline body text:
    { sectionNumber: "7.10" }
    { subsectionNumber: "7.10" }
- Single rule embedded inside a rulebook section:
    { ruleNumber: "7.4.1" }
- Selected clauses within a specific rule (scoped before clause selection):
    { ruleNumber: "7.1.1", clausePaths: [["1"], ["2"]] }
- Numbered paragraphs under an exact named guidance heading:
    { sectionNumber: "7.1", guidanceHeading: "Guidance on the customer risk assessment",
      guidanceNumbers: ["1", "2", "3"] }
- Several bullets, retaining their introduction and source order:
    { sectionNumber: "4.2.1", bulletStartsWithAny: ["Describes the weighting", "Determines the residual risk"] }
- Single numbered guidance paragraph:
    { chapter: "Chapter 2", articleLabel: "Guidance", guidanceNumber: "14" }
- Optional narrowing by chapter:
    { chapter: "Part IV", sectionNumber: "7.10" }
- Optional note shown under the selected section title:
    { articleNumber: "24", note: "Why this section matters for this topic." }
- Optional topic keywords highlighted in all right-panel excerpts:
    keywords: ["SAR", "suspicious transaction"]
- Single definition inside a definitions article:
    { articleNumber: "1", definitionTerm: "Suspicious Transactions" }
    { articleNumber: "1", definitionTerm: "Beneficial Owner" }
- Single nested clause inside an article:
    { articleNumber: "5", clausePath: ["2", "c", "5"] }
  The clause path follows the visible hierarchy, for example Article (5) -> 2. -> c. -> 5).
- Multiple clauses in one article excerpt:
    { articleNumber: "5", clausePaths: [["3"], ["4"]] }
- Single bullet with its heading and introductory text:
    { chapter: "Part III", sectionNumber: "1", headingText: "The Elements of an AML/CFT Program",
      bulletStartsWith: "A designated compliance function" }
  Omit headingText to retain the section's introductory text before its first bullet.

Available documentId values:
- federal-decree-law-10-2025
  Hierarchy: chapter -> article
  Reference examples:
    { chapter: "Chapter Four" }
    { articleNumber: "24" }
    { chapter: "Chapter Four", articleNumber: "24" }
    { articleLabel: "Article (24)" }
    { articleNumber: "1", definitionTerm: "Suspicious Transactions" }

- cabinet-resolution-134-2025
  Hierarchy: chapter -> article
  Reference examples:
    { chapter: "Chapter Five" }
    { articleNumber: "24" }
    { chapter: "Chapter Five", articleNumber: "24" }
    { articleLabel: "Article (24)" }
    { articleNumber: "1", definitionTerm: "Beneficial Owner" }
    { articleNumber: "5", clausePath: ["2", "c", "5"] }

- cabinet-decision-74-2020
  Hierarchy: chapter/part -> article
  Reference examples:
    { chapter: "Chapter One" }
    { articleNumber: "1" }
    { articleLabel: "Article (1)" }

- cabinet-resolution-109-2023
  Hierarchy: document -> article
  Reference examples:
    { articleNumber: "1" }
    { articleLabel: "Article (1)" }

- cabinet-resolution-111-2022
  Hierarchy: document -> article
  Reference examples:
    { articleNumber: "1" }
    { articleLabel: "Article (1)" }

- federal-law-7-2014
  Hierarchy: chapter/section -> article
  Reference examples:
    { chapter: "Chapter One" }
    { articleNumber: "25" }
    { articleLabel: "Article (25)" }

- fi-aml-cft-guidelines-2021
  Hierarchy: part -> top-level section -> embedded sub-section headings in body
  Reference examples:
    { chapter: "Part IV" }
    { articleNumber: "7" }
    { articleLabel: "Section 7" }
    { sectionNumber: "7.10" }
    { chapter: "Part IV", sectionNumber: "7.10" }

- dnfbp-aml-cft-guidelines-2021
  Hierarchy: part -> top-level section -> embedded sub-section headings in body
  Reference examples:
    { chapter: "Part IV" }
    { articleNumber: "7" }
    { articleLabel: "Section 7" }
    { sectionNumber: "7.10" }
    { chapter: "Part IV", sectionNumber: "7.10" }

- dfsa-aml-module
  Hierarchy: chapter -> section -> rule/guidance content
  Reference examples:
    { chapter: "Chapter 13" }
    { sectionNumber: "13.2" }
    { sectionLabel: "Section 13.2" }
    { chapter: "Chapter 13", articleNumber: "13.2" }
    { ruleNumber: "7.4.1" }
    { sectionNumber: "3.2", definitionTerm: "State" }
    { chapter: "Chapter 2", articleLabel: "Guidance", guidanceNumber: "14" }

- fsra-aml-rulebook
  Hierarchy: chapter -> section -> rule/guidance content
  Reference examples:
    { chapter: "Chapter 13" }
    { sectionNumber: "13.2" }
    { sectionLabel: "Section 13.2" }
    { chapter: "Chapter 13", articleNumber: "13.2" }
    { ruleNumber: "7.4.1" }

- fatf-beneficial-ownership-2024
  Hierarchy: section -> subsection/content
  The topic registry targets top-level sections/subsections stored as articles.
  Reference examples:
    { sectionNumber: "2" }
    { sectionNumber: "2.1" }
    { sectionLabel: "Section 2.1" }
*/

const regulatoryTopics = {
  "sow-sof": {
  "id": "sow-sof",
  "label": "SoW/SoF",
  "summary": "Establishing, substantiating and reviewing source of wealth and source of funds.",
  "keywords": [
    "source of wealth",
    "source of funds",
    "source funds",
    "wealth",
    "funds"
  ],
  "documents": [
    {
      "documentId": "cabinet-resolution-134-2025",
      "sections": [
        {
          "articleNumber": "5",
          "clausePath": [
            "2",
            "c",
            "4"
          ]
        },
        {
          "articleNumber": "8",
          "clausePath": [
            "1"
          ]
        },
        {
          "articleNumber": "16",
          "clausePaths": [
            [
              "1",
              "a",
              "3"
            ],
            [
              "1",
              "b"
            ]
          ]
        }
      ]
    },
    {
      "documentId": "fi-aml-cft-guidelines-2021",
      "sections": [
        {
          "sectionNumber": "6.4",
          "textStartsWithAny": [
            "More detailed inquiry and evaluation",
            "Source of funds (revenue)",
            "Occupation or type of business",
            "Financial statements",
            "Banking references",
            "Description of the customer’s primary trade area",
            "Description of the business operations",
            "Explanations for changes in"
          ]
        },
        {
          "sectionNumber": "6.4.1",
          "textStartsWithAny": [
            "If a customer, Beneficial Owner, beneficiary, or controlling person is identified as a PEP",
            "With regard to identified Domestic PEPs",
            "The handling of a customer who is no longer entrusted"
          ]
        },
        {
          "sectionNumber": "6.4.2",
          "bulletStartsWithAny": [
            "Consistency between the nature of the customer’s business",
            "The level of complexity and transparency of the customer’s transactions",
            "The nature of any other business interests",
            "Consistency between the customer’s line of business"
          ]
        },
        {
          "sectionNumber": "6.4.3",
          "bulletStartsWith": "More detailed inquiry and evaluation"
        },
        {
          "sectionNumber": "6.5",
          "bulletStartsWith": "Fewer and less detailed inquiries"
        },
        {
          "sectionNumber": "6.3.6",
          "textStartsWithAny": [
            "When the source of wealth"
          ]
        }
      ]
    },
    {
      "documentId": "dnfbp-aml-cft-guidelines-2021",
      "sections": [
        {
          "sectionNumber": "6.4",
          "textStartsWithAny": [
            "More detailed inquiry and evaluation",
            "Source of funds (revenue)",
            "Occupation or type of business",
            "Financial statements",
            "Banking references",
            "Description of the customer’s primary trade area",
            "Description of the business operations",
            "Explanations for changes in"
          ]
        },
        {
          "sectionNumber": "6.4.1",
          "textStartsWithAny": [
            "If a customer, Beneficial Owner, beneficiary, or controlling person is identified as a PEP",
            "With regard to identified Domestic PEPs",
            "The handling of a customer who is no longer entrusted"
          ]
        },
        {
          "sectionNumber": "6.4.2",
          "bulletStartsWithAny": [
            "Consistency between the nature of the customer’s business",
            "The level of complexity and transparency of the customer’s transactions",
            "The nature of any other business interests",
            "Consistency between the customer’s line of business"
          ]
        },
        {
          "sectionNumber": "6.4.3",
          "bulletStartsWith": "More detailed inquiry and evaluation"
        },
        {
          "sectionNumber": "6.5",
          "bulletStartsWith": "Fewer and less detailed inquiries"
        },
        {
          "sectionNumber": "6.3.4",
          "textStartsWithAny": [
            "When the source of wealth"
          ]
        }
      ]
    },
    {
      "documentId": "dfsa-aml-module",
      "sections": [
        {
          "sectionNumber": "3.2",
          "definitionTerms": [
            "source of funds",
            "source of wealth"
          ]
        },
        {
          "ruleNumber": "7.3.8",
          "clausePaths": [
            [
              "2",
              "b"
            ],
            [
              "3",
              "b"
            ],
            [
              "4"
            ]
          ]
        },
        {
          "ruleNumber": "7.4.1",
          "clausePath": [
            "c"
          ]
        },
        {
          "sectionNumber": "7.4",
          "guidanceHeading": "Guidance",
          "guidanceTitle": "Source of wealth and funds guidance",
          "guidanceNumbers": [
            "1",
            "5",
            "6",
            "7",
            "8"
          ]
        }
      ]
    },
    {
      "documentId": "fsra-aml-rulebook",
      "sections": [
        {
          "sectionNumber": "3.2",
          "definitionTerms": [
            "Source of Funds",
            "Source of Wealth"
          ]
        },
        {
          "ruleNumber": "8.4.1",
          "clausePaths": [
            [
              "1"
            ],
            [
              "2",
              "c"
            ]
          ]
        },
        {
          "sectionNumber": "8.4",
          "guidanceHeading": "Guidance",
          "guidanceTitle": "Source of wealth and funds guidance",
          "guidanceNumbers": [
            "1",
            "5",
            "6",
            "7"
          ]
        }
      ]
    }
  ]
},
  "periodic-review": {
    "id": "periodic-review",
    "label": "Periodic review",
    "summary": "Scheduled and event-driven reviews of customer due diligence files and risk classifications.",
    "keywords": [
      "periodic",
      "review",
      "update",
      "updating"
    ],
    "documents": [
      {
        "documentId": "cabinet-resolution-134-2025",
        "sections": [
          {
            "articleNumber": "5",
            "clausePaths": [
              [
                "2",
                "c",
                "3"
              ],
              [
                "3",
                "b"
              ]
            ]
          },
          {
            "articleNumber": "7",
            "clausePaths": [
              [
                "1",
                "b"
              ],
              [
                "1",
                "c"
              ]
            ]
          },
          {
            "articleNumber": "8",
            "clausePath": [
              "2"
            ]
          }
        ]
      },
      {
        "documentId": "fi-aml-cft-guidelines-2021",
        "sections": [
          {
            "sectionNumber": "6.3.6"
          }
        ]
      },
      {
        "documentId": "dnfbp-aml-cft-guidelines-2021",
        "sections": [
          {
            "sectionNumber": "6.3.4"
          }
        ]
      },
      {
        "documentId": "dfsa-aml-module",
        "sections": [
          {
            "ruleNumber": "7.6.1",
            "clausePaths": [
              [
                "1",
                "d"
              ],
              [
                "1",
                "e"
              ],
              [
                "2"
              ]
            ]
          },
          {
            "sectionNumber": "7.6",
            "guidanceHeading": "Guidance",
            "guidanceTitle": "Periodic customer review guidance",
            "guidanceNumbers": [
              "1",
              "2",
              "3"
            ]
          },
          {
            "ruleNumber": "7.6.2"
          }
        ]
      },
      {
        "documentId": "fsra-aml-rulebook",
        "sections": [
          {
            "ruleNumber": "8.6.1",
            "clausePaths": [
              [
                "d"
              ],
              [
                "e"
              ]
            ]
          },
          {
            "sectionNumber": "8.6",
            "guidanceHeading": "Guidance",
            "guidanceTitle": "Periodic customer review guidance",
            "guidanceNumbers": [
              "1",
              "2",
              "3",
              "4"
            ]
          },
          {
            "ruleNumber": "8.6.3"
          }
        ]
      }
    ]
  },
  "risk-factors": {
    "id": "risk-factors",
    "label": "Risk factors",
    "summary": "Characteristics and circumstances affecting financial-crime risk.",
    "keywords": [
      "risk factor",
      "high-risk",
      "higher risk",
      "lower risk"
    ],
    "documents": [
      {
        "documentId": "cabinet-resolution-134-2025",
        "sections": [
          {
            "articleNumber": "1",
            "definitionTerm": "High-Risk Customers"
          },
          {
            "articleNumber": "5",
            "clausePath": [
              "1",
              "a"
            ]
          },
          {
            "articleNumber": "24"
          }
        ]
      },
      {
        "documentId": "fi-aml-cft-guidelines-2021",
        "sections": [
          {
            "sectionNumber": "4.1.2"
          },
          {
            "sectionNumber": "4.1.3"
          },
          {
            "sectionNumber": "4.1.4"
          },
          {
            "sectionNumber": "4.1.5"
          },
          {
            "sectionNumber": "4.1.6"
          },
          {
            "sectionNumber": "4.1.7"
          },
          {
            "sectionNumber": "4.1.8"
          }
        ]
      },
      {
        "documentId": "dnfbp-aml-cft-guidelines-2021",
        "sections": [
          {
            "sectionNumber": "4.1.2"
          },
          {
            "sectionNumber": "4.1.3"
          },
          {
            "sectionNumber": "4.1.4"
          },
          {
            "sectionNumber": "4.1.5"
          },
          {
            "sectionNumber": "4.1.6"
          },
          {
            "sectionNumber": "4.1.7"
          },
          {
            "sectionNumber": "4.1.8"
          }
        ]
      },
      {
        "documentId": "dfsa-aml-module",
        "sections": [
          {
            "ruleNumber": "5.1.1",
            "clausePaths": [
              [
                "b"
              ]
            ]
          },
          {
            "ruleNumber": "6.1.1",
            "clausePaths": [
              [
                "3"
              ]
            ]
          },
          {
            "ruleNumber": "6.1.2"
          },
          {
            "ruleNumber": "6.1.3"
          }
        ]
      },
      {
        "documentId": "fsra-aml-rulebook",
        "sections": [
          {
            "ruleNumber": "6.1.1",
            "clausePaths": [
              [
                "b"
              ]
            ]
          },
          {
            "ruleNumber": "7.1.1",
            "clausePaths": [
              [
                "3"
              ]
            ]
          },
          {
            "ruleNumber": "7.1.2",
            "clausePaths": [
              [
                "1"
              ]
            ]
          },
          {
            "ruleNumber": "7.1.3",
            "clausePaths": [
              [
                "1"
              ]
            ]
          },
          {
            "ruleNumber": "7.1.4"
          }
        ]
      }
    ]
  },
  "risk-rating": {
    "id": "risk-rating",
    "label": "Risk rating",
    "summary": "Assigning, documenting and reviewing risk classifications.",
    "keywords": [
      "risk rating",
      "risk classification",
      "weighting",
      "residual risk"
    ],
    "documents": [
      {
        "documentId": "federal-decree-law-10-2025",
        "sections": [
          {
            "articleNumber": "19",
            "clausePath": [
              "1",
              "a"
            ]
          }
        ]
      },
      {
        "documentId": "cabinet-resolution-134-2025",
        "sections": [
          {
            "articleNumber": "5",
            "clausePath": [
              "1",
              "b"
            ]
          }
        ]
      },
      {
        "documentId": "fi-aml-cft-guidelines-2021",
        "sections": [
          {
            "sectionNumber": "6.1"
          },
          {
            "sectionNumber": "6.1.1"
          },
          {
            "sectionNumber": "4.2"
          },
          {
            "sectionNumber": "4.2.1",
            "bulletStartsWithAny": [
              "Describes the weighting",
              "Evaluates the likelihood",
              "Takes into account whether the AML/CFT controls",
              "Determines the effectiveness",
              "Determines the residual risk",
              "Establishes based on the residual risk",
              "Determines the rationale",
              "Is properly documented",
              "Is tested and audited"
            ],
            "note": "Business-wide risk assessment methodology."
          },
          {
            "sectionNumber": "4.2.2",
            "note": "Documentation and review of the business-wide assessment."
          }
        ]
      },
      {
        "documentId": "dnfbp-aml-cft-guidelines-2021",
        "sections": [
          {
            "sectionNumber": "6.1"
          },
          {
            "sectionNumber": "6.1.1"
          },
          {
            "sectionNumber": "4.2"
          },
          {
            "sectionNumber": "4.2.1",
            "bulletStartsWithAny": [
              "Describes the weighting",
              "Evaluates the likelihood",
              "Takes into account whether the AML/CFT controls",
              "Determines the effectiveness",
              "Determines the residual risk",
              "Establishes based on the residual risk",
              "Determines the rationale",
              "Is properly documented",
              "Is tested and audited"
            ],
            "note": "Business-wide risk assessment methodology."
          },
          {
            "sectionNumber": "4.2.2",
            "note": "Documentation and review of the business-wide assessment."
          }
        ]
      },
      {
        "documentId": "dfsa-aml-module",
        "sections": [
          {
            "ruleNumber": "6.1.1",
            "clausePaths": [
              [
                "1"
              ],
              [
                "2"
              ]
            ]
          },
          {
            "sectionNumber": "6.1",
            "guidanceHeading": "Guidance on the customer risk assessment",
            "guidanceNumbers": [
              "1",
              "2"
            ]
          }
        ]
      },
      {
        "documentId": "fsra-aml-rulebook",
        "sections": [
          {
            "ruleNumber": "7.1.1",
            "clausePaths": [
              [
                "1"
              ],
              [
                "2"
              ]
            ]
          },
          {
            "ruleNumber": "7.1.2",
            "clausePaths": [
              [
                "2"
              ],
              [
                "3"
              ]
            ]
          },
          {
            "sectionNumber": "7.1",
            "guidanceHeading": "Guidance on the customer risk assessment",
            "guidanceNumbers": [
              "1",
              "2",
              "3"
            ]
          }
        ]
      }
    ]
  },
  training: {
    id: "training",
    label: "Training",
    summary: "References concerning training, staff awareness and related AML/CFT controls.",
    keywords: ["training", "awareness"],
    documents: [
      {
        documentId: "federal-decree-law-10-2025",
        sections: [{ articleNumber: "3", clausePath: ["1", "c"] }],
      },
      {
        documentId: "cabinet-resolution-134-2025",
        sections: [{ articleNumber: "22", clausePath: ["4"] }],
      },
      ...["fi-aml-cft-guidelines-2021", "dnfbp-aml-cft-guidelines-2021"].map((documentId) => ({
        documentId,
        sections: [
          { sectionNumber: "3.10" },
          {
            chapter: "Part III",
            sectionNumber: "1",
            headingText: "The Elements of an AML/CFT Program",
            bulletStartsWith: "A system of internal policies, procedures and controls",
          },
          {
            sectionNumber: "5",
            bulletStartsWith: "AML/CFT governance, including compliance staffing and training",
          },
          { sectionNumber: "7.4" },
          { sectionNumber: "7.7" },
          { sectionNumber: "7.8" },
          { sectionNumber: "8.1.2" },
          { sectionNumber: "8.2" },
          { sectionNumber: "8.4" },
          { sectionNumber: "8.5" },
        ],
      })),
    ],
  },
  mlro: {
    id: "mlro",
    label: "MLRO",
    summary: "References for the appointment, independence and responsibilities of the compliance officer.",
    keywords: ["Compliance Officer", "money laundering reporting officer", "MLRO", "compliance function"],
    documents: [
      {
        documentId: "cabinet-resolution-134-2025",
        sections: [
          { articleNumber: "21", clausePath: ["3"] },
          { articleNumber: "22" },
        ],
      },
      {
        documentId: "fi-aml-cft-guidelines-2021",
        sections: [
          { sectionNumber: "8.1.1" },
          { sectionNumber: "8.1.2" },
          {
            chapter: "Part III",
            sectionNumber: "1",
            headingText: "The Elements of an AML/CFT Program",
            bulletStartsWith: "A designated compliance function",
          },
        ],
      },
      {
        documentId: "dnfbp-aml-cft-guidelines-2021",
        sections: [
          { sectionNumber: "8.1.1" },
          { sectionNumber: "8.1.2" },
          {
            chapter: "Part III",
            sectionNumber: "1",
            headingText: "The Elements of an AML/CFT Program",
            bulletStartsWith: "A designated compliance function",
          },
        ],
      },
    ],
  },
  sanctions: {
    id: "sanctions",
    label: "Sanctions",
    summary: "References for targeted financial sanctions, freezing duties and related obligations.",
    keywords: ["Targeted Financial Sanctions", "sanctions"],
    documents: [
      {
        documentId: "federal-decree-law-10-2025",
        sections: [
          { articleNumber: "1", definitionTerm: "Targeted Financial Sanctions" },
          { articleNumber: "19" },
          { articleNumber: "33" },
        ],
      },
      {
        documentId: "cabinet-resolution-134-2025",
        sections: [
          { articleNumber: "5", clausePaths: [["3"], ["4"]] },
          { articleNumber: "28" },
          { articleNumber: "36" },
        ],
      },
      {
        documentId: "cabinet-decision-74-2020",
        sections: [
          { articleNumber: "1" },
          { articleNumber: "15" },
          { articleNumber: "16" },
          { articleNumber: "17" },
          { articleNumber: "19" },
          { articleNumber: "21" },
          { articleNumber: "23", clausePath: ["3"] },
        ],
      },
    ],
  },
  peps: {
    id: "peps",
    label: "PEPs",
    summary: "References for politically exposed persons and related due diligence requirements.",
    keywords: ["Politically Exposed Persons", "Politically Exposed Person", "PEPs", "PEP"],
    documents: [
      {
        documentId: "cabinet-resolution-134-2025",
        sections: [
          { articleNumber: "1", definitionTerm: "Politically Exposed Persons (PEPs)" },
          { articleNumber: "16" },
          { articleNumber: "20" },
        ],
      },
      {
        documentId: "fi-aml-cft-guidelines-2021",
        sections: [
          { sectionNumber: "6.4" },
          { sectionNumber: "6.4.1" },
          { sectionNumber: "11.1", definitionTerm: "Money Laundering" },
        ],
      },
      {
        documentId: "dnfbp-aml-cft-guidelines-2021",
        sections: [
          { sectionNumber: "6.4" },
          { sectionNumber: "6.4.1" },
          { sectionNumber: "11.1", definitionTerm: "Money Laundering" },
        ],
      },
    ],
  },
  sars: {
    id: "sars",
    label: "SARs",
    summary: "Curated references for suspicious activity and suspicious transaction reporting.",
    keywords: ["SAR", "STRs", "STR", "suspicious transaction","suspicious transaction report", "Suspicious Activity Report", "Suspicious Activity", "suspicious activity or transactions", "transactions or activities appear unusual or suspicious"],
    documents: [
      {
        documentId: "federal-decree-law-10-2025",
        sections: [
          {
            articleNumber: "1",
            definitionTerm: "Suspicious Transactions",
            // note: "Example curated reference. Replace or extend this mapping manually.",
          },
          {
            articleNumber: "5",
            // note: "Example curated reference. Replace or extend this mapping manually.",
          },
          {
            articleNumber: "11",
            // note: "Example curated reference. Replace or extend this mapping manually.",
          },
        ],
      },
      {
        documentId: "cabinet-resolution-134-2025",
        sections: [
          {
            articleNumber: "5",
            clausePath: ["2", "c", "5"],
            // note: "Example nested clause reference.",
          },
          {
            articleNumber: "14",
            // note: "Example curated reference. Replace or extend this mapping manually.",
          },
          {
            articleNumber: "18",
            // note: "Example curated reference. Replace or extend this mapping manually.",
          },
          {
            articleNumber: "19",
            // note: "Example curated reference. Replace or extend this mapping manually.",
          },
        ],
      },
      {
        documentId: "dfsa-aml-module",
        sections: [
          {
            chapter: "Chapter 2",
            articleLabel: "Guidance",
            guidanceNumber: "14",
          },
          {
            chapter: "Chapter 13",
          },
          {
            sectionNumber: "3.2",
            definitionTerm: "State",
          },
          {
            ruleNumber: "7.4.1",
          },
          {
            ruleNumber: "7.7.1",
          },
          {
            ruleNumber: "8.3.1",
          },
          {
            ruleNumber: "10.3.1",
          },
          {
            ruleNumber: "11.4.1",
          },
          {
            ruleNumber: "12.1.1",
          },
          {
            ruleNumber: "14.4.1",
          },
        ],
      },
      {
        documentId: "fi-aml-cft-guidelines-2021",
        sections: [
          {
            sectionNumber: "3.1",
          },
          {
            sectionNumber: "3.2",
          },
          {
            sectionNumber: "3.3",
          },
          {
            sectionNumber: "3.5",
          },
          {
            sectionNumber: "3.11",
          },
          {
            sectionNumber: "5",
          },
          {
            sectionNumber: "6.1.2",
          },
          {
            sectionNumber: "6.3.5",
          },
          {
            sectionNumber: "7",
          },
          {
            sectionNumber: "8.2",
          },
          {
            sectionNumber: "9.2.6",
          },
          {
            sectionNumber: "11.1",
            definitionTerm: "Suspicious Transactions",
          },
        ],
      },
      {
        documentId: "dnfbp-aml-cft-guidelines-2021",
        sections: [
          {
            sectionNumber: "3.1",
          },
          {
            sectionNumber: "3.2",
          },
          {
            sectionNumber: "3.3",
          },
          {
            sectionNumber: "3.5",
          },
          {
            sectionNumber: "3.11",
          },
          {
            sectionNumber: "5",
          },
          {
            sectionNumber: "7",
            // note: "Example curated reference. Replace or extend this mapping manually.",
          },
          {
            sectionNumber: "8.2",
          },
          {
            sectionNumber: "8.3",
          },
          {
            sectionNumber: "8.4",
          },
          {
            sectionNumber: "8.5",
          },
          {
            sectionNumber: "8.6",
          },
          {
            sectionNumber: "9.2.1",
          },
          {
            sectionNumber: "9.2.6",
          },
          {
            sectionNumber: "11.1",
            definitionTerm: "Suspicious Transactions",
          },
        ],
      },
    ],
  },
  "record-keeping": {
    id: "record-keeping",
    label: "Record keeping",
    keywords: ["record", "retain", "retention"],
    documents: [
      {
        documentId: "federal-decree-law-10-2025",
        sections: [
          {
            articleNumber: "19",
            clausePaths: [["1", "a"], ["1", "b"], ["1", "f"]],
          },
        ],
      },
      {
        documentId: "cabinet-resolution-134-2025",
        sections: [
          {
            articleNumber: "25",
            // note: "Starter reference for record-keeping under the implementing rules.",
          },
        ],
      },
      {
        documentId: "fi-aml-cft-guidelines-2021",
        sections: [{ sectionNumber: "9" }],
      },
      {
        documentId: "dnfbp-aml-cft-guidelines-2021",
        sections: [{ sectionNumber: "9" }],
      },
      {
        documentId: "dfsa-aml-module",
        sections: [
          {
            sectionNumber: "14.4",
            // note: "DFSA AML Module record keeping section.",
          },
        ],
      },
      {
        documentId: "fsra-aml-rulebook",
        sections: [
          {
            sectionNumber: "4.5",
            // note: "FSRA AML Rulebook record keeping section.",
          },
        ],
      },
    ],
  },
};

export default regulatoryTopics;
